import { TextFieldModule } from '@angular/cdk/text-field';
import { CurrencyPipe, registerLocaleData } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import localeFiExtra from '@angular/common/locales/extra/fi';
import localeFi from '@angular/common/locales/fi';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MomentDateAdapter, MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AvatarModule } from 'ngx-avatar';
import { MarkdownModule } from 'ngx-markdown';

import { environment } from '../environments/environment';
import { ApiModule } from './api/api.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AutofocusDirective } from './autofocus.directive';
import { AdvanceInvoiceNumberDialogComponent } from './components/advance-invoice-number-dialog/advance-invoice-number-dialog.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { CardTitleComponent } from './components/card-title/card-title.component';
import { CardWidgetComponent } from './components/card-widget/card-widget.component';
import { ClientEditDialogComponent } from './components/client-edit-dialog/client-edit-dialog.component';
import { ClientHistoryComponent } from './components/client-history/client-history.component';
import { CompanyFormFieldsComponent } from './components/company-form-fields/company-form-fields.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { EmailStateLabelComponent } from './components/email-state-label/email-state-label.component';
import { FullScreenLoaderComponent } from './components/full-screen-loader/full-screen-loader.component';
import { InvoiceConfigurationListFilterComponent } from './components/invoice-configuration-list-filter/invoice-configuration-list-filter.component';
import { InvoiceNotificationsComponent } from './components/invoice-notifications/invoice-notifications.component';
import { InvoiceStateListFilterComponent } from './components/invoice-state-list-filter/invoice-state-list-filter.component';
import { LightEntrepreneurClientEditDialogComponent } from './components/light-entrepreneur-client-edit-dialog/light-entrepreneur-client-edit-dialog.component';
import { ListFilterGroupComponent } from './components/list-filter-group/list-filter-group.component';
import { ListLoaderComponent } from './components/list-loader/list-loader.component';
import { LoaderComponent } from './components/loader/loader.component';
import { LoginComponent } from './components/login/login.component';
import { MainBarComponent } from './components/main-bar/main-bar.component';
import { MainNavComponent } from './components/main-nav/main-nav.component';
import { MultiSelectFilterComponent } from './components/multi-select-filter/multi-select-filter.component';
import { MultiSelectComponent } from './components/multi-select/multi-select.component';
import { OutgoingPaymentStatusLabelComponent } from './components/outgoing-payment-status-label/outgoing-payment-status-label.component';
import { PageBarComponent } from './components/page-bar/page-bar.component';
import { PageComponent } from './components/page/page.component';
import { PaymentTransferStatusLabelComponent } from './components/payment-transfer-status-label/payment-transfer-status-label.component';
import { ResetListFilterComponent } from './components/reset-list-filter/reset-list-filter.component';
import { ClientAutoCompleteComponent } from './components/resource-auto-complete/client-auto-complete.component';
import { EndCustomerAutoCompleteComponent } from './components/resource-auto-complete/end-customer-auto-complete.component';
import { LeadSourceAutoCompleteComponent } from './components/resource-auto-complete/lead-source-auto-complete.component';
import { PartnerAutoCompleteComponent } from './components/resource-auto-complete/partner-auto-complete.component';
import { ResourceAutoCompleteComponent } from './components/resource-auto-complete/resource-auto-complete.component';
import { ReturnUrlRedirectComponent } from './components/return-url-redirect/return-url-redirect.component';
import { SignDocumentRequestDialogComponent } from './components/sign-document-request-dialog/sign-document-request-dialog.component';
import { SourceNameComponent } from './components/source-name/source-name.component';
import { TextListFilterComponent } from './components/text-list-filter/text-list-filter.component';
import { TextComponent } from './components/text/text.component';
import { UserInfoComponent } from './components/user-info/user-info.component';
import { CoreModule } from './core/core.module';
import { FileUploadDirective } from './file-upload.directive';
import { JsonDateInterceptor } from './interceptor/jsondate.interceptor';
import { MainLayoutComponent } from './layouts/main-layout.component';
import { MaterialModule } from './material.module';
import { CompanyLookupResource } from './model/lookup-resource';
import {
  CountriesMapCache,
  EmailStatesMapCache,
  FinancingMethodsMapCache,
  InvoiceAttachmentTypesMapCache,
  InvoiceConfigurationsMapCache,
  InvoiceStatesMapCache,
  PaymentTransferStatusesMapCache,
  PenaltyInterestMethodsMapCache,
  UsersMapCache
} from './model/model-map-cache';
import {
  ApplicationsResource,
  ClientRepresentativesResource,
  ClientsResource,
  CountriesResource,
  CreditInsurancesResource,
  EmailLogsResource, EmailStatesResource,
  EmailTemplatesResource,
  EndCustomerContactsResource,
  EndCustomersResource,
  FinancingMethodsResource,
  InvoiceAttachmentsResource,
  InvoiceAttachmentTypesResource,
  InvoiceConfigurationsResource,
  InvoicesResource,
  InvoiceStateLogsResource,
  InvoiceStatesResource,
  LeadInformationsResource,
  LeadSourcesResource,
  NotificationLogsResource,
  PartnersResource,
  PaymentHistoriesResource, PaymentHistoryImportsResource,
  PaymentTransferStatusesResource,
  PenaltyInterestMethodsResource,
  SmsLogsResource,
  SmsTemplatesResource,
  UsersResource
} from './model/model-resource';
import { ClearPageComponent } from './page/clear-page/clear-page.component';
import { ClientPageComponent } from './page/client-page/client-page.component';
import { ClientRepresentativePageComponent } from './page/client-representative-page/client-representative-page.component';
import { ClientsListPageComponent } from './page/clients-list-page/clients-list-page.component';
import { ClientsPageComponent } from './page/clients-page/clients-page.component';
import { DashboardPageComponent } from './page/dashboard-page/dashboard-page.component';
import { EmailTemplatePageComponent } from './page/email-template-page/email-template-page.component';
import { EndCustomerDuplicatesPageComponent } from './page/end-customer-duplicates-page/end-customer-duplicates-page.component';
import { EndCustomerMergePageComponent } from './page/end-customer-merge-page/end-customer-merge-page.component';
import { IncomingPaymentsListPageComponent } from './page/incoming-payments-list-page/incoming-payments-list-page.component';
import { InvoiceCreatePageComponent } from './page/invoice-create-page/invoice-create-page.component';
import { InvoicePageComponent } from './page/invoice-page/invoice-page.component';
import { InvoiceSubPageRollbackGuard } from './page/invoice-sub-page-rollback.guard';
import { InvoicesListPageComponent } from './page/invoices-list-page/invoices-list-page.component';
import { LeadInformationsListPageComponent } from './page/lead-informations-list-page/lead-informations-list-page.component';
import { LeadSourcePageComponent } from './page/lead-source-page/lead-source-page.component';
import { LeadSourcesListPageComponent } from './page/lead-sources-list-page/lead-sources-list-page.component';
import { LightEntrepreneurClientPageComponent } from './page/light-entrepreneur-client-page/light-entrepreneur-client-page.component';
import { NotFoundPageComponent } from './page/not-found-page/not-found-page.component';
import { OperationalTasksListPageComponent } from './page/operational-tasks-list-page/operational-tasks-list-page.component';
import { OutgoingPaymentsListPageComponent } from './page/outgoing-payments-list-page/outgoing-payments-list-page.component';
import { PartnerPageComponent } from './page/partner-page/partner-page.component';
import { PartnerSellerPageComponent } from './page/partner-seller-page/partner-seller-page.component';
import { PartnersListPageComponent } from './page/partners-list-page/partners-list-page.component';
import { PaymentHistoriesListPageComponent } from './page/payment-histories-list-page/payment-histories-list-page.component';
import { PaymentHistoryImportPageComponent } from './page/payment-history-import-page/payment-history-import-page.component';
import { PcrTasksListPageComponent } from './page/pcr-tasks-list-page/pcr-tasks-list-page.component';
import { PermissionGuard } from './page/permission-guard';
//import {RouteGuard} from './page/route-guard';
import { SettingsPageComponent } from './page/settings-page/settings-page.component';
import { SmsTemplatePageComponent } from './page/sms-template-page/sms-template-page.component';
import { StranglerGuard } from './page/strangler-guard';
import { TemplatesListPageComponent } from './page/templates-list-page/templates-list-page.component';
import { LabelPipe } from './pipe/label.pipe';
import { MoneyPipe } from './pipe/money.pipe';
import { AlertService } from './service/alert.service';
import { SharedModule } from './shared/shared.module';
import { ClientExportsModalComponent } from './widget/client-exports-widget/client-exports-modal/client-exports-modal.component';
import { ClientExportsWidgetComponent } from './widget/client-exports-widget/client-exports-widget.component';
import { InvoiceAiChatWidgetComponent } from './widget/invoice-ai-chat-widget/invoice-ai-chat-widget.component';
import { InvoiceAttachmentsV2WidgetComponent } from './widget/invoice-attachments-v2-widget/invoice-attachments-v2-widget.component';
import { InvoiceAttachmentsWidgetComponent } from './widget/invoice-attachments-widget/invoice-attachments-widget.component';
import { InvoiceClientInvoicesWidgetComponent } from './widget/invoice-client-invoices-widget/invoice-client-invoices-widget.component';
import { InvoiceClientWidgetComponent } from './widget/invoice-client-widget/invoice-client-widget.component';
import { InvoiceCommentsV2WidgetComponent } from './widget/invoice-comments-v2-widget/invoice-comments-v2-widget.component';
import { InvoiceEmailV2WidgetComponent } from './widget/invoice-email-v2-widget/invoice-email-v2-widget.component';
import { InvoiceEmailWidgetComponent } from './widget/invoice-email-widget/invoice-email-widget.component';
import { InvoiceEndCustomerCreditInsuranceWidgetComponent } from './widget/invoice-end-customer-credit-insurance-widget/invoice-end-customer-credit-insurance-widget.component';
import { InvoiceEndCustomerInvoicesWidgetComponent } from './widget/invoice-end-customer-invoices-widget/invoice-end-customer-invoices-widget.component';
import { InvoiceEndCustomerWidgetComponent } from './widget/invoice-end-customer-widget/invoice-end-customer-widget.component';
import { InvoiceFinancingHoldsWidgetComponent } from './widget/invoice-financing-holds-widget/invoice-financing-holds-widget.component';
import { PlaceFinancingHoldDialogComponent } from './widget/invoice-financing-holds-widget/place-financing-hold-dialog/place-financing-hold-dialog.component';
import { ReleaseFinancingHoldDialogComponent } from './widget/invoice-financing-holds-widget/release-financing-hold-dialog/release-financing-hold-dialog.component';
import { ReleaseFinancingHoldsDialogComponent } from './widget/invoice-financing-holds-widget/release-financing-holds-dialog/release-financing-holds-dialog.component';
import { InvoiceFinancingV2WidgetComponent } from './widget/invoice-financing-v2-widget/invoice-financing-v2-widget.component';
import { InvoiceFinancingWidgetComponent } from './widget/invoice-financing-widget/invoice-financing-widget.component';
import { InvoiceInfoV2WidgetComponent } from './widget/invoice-info-v2-widget/invoice-info-v2-widget.component';
import { InvoiceInfoWidgetComponent } from './widget/invoice-info-widget/invoice-info-widget.component';
import { InvoiceLeClientInvoicesWidgetComponent } from './widget/invoice-le-client-invoices-widget/invoice-le-client-invoices-widget.component';
import { InvoiceLeClientWidgetComponent } from './widget/invoice-le-client-widget/invoice-le-client-widget.component';
import { InvoiceNotificationsWidgetComponent } from './widget/invoice-notifications-widget/invoice-notifications-widget.component';
import { InvoiceOutgoingPaymentWidgetComponent } from './widget/invoice-outgoing-payment-widget/invoice-outgoing-payment-widget.component';
import { InvoicePaymentHistoryWidgetComponent } from './widget/invoice-payment-history-widget/invoice-payment-history-widget.component';
import { InvoicePcrWidgetComponent } from './widget/invoice-pcr-widget/invoice-pcr-widget.component';
import { InvoicePresenceRosterWidgetComponent } from './widget/invoice-presence-roster-widget/invoice-presence-roster-widget.component';
import { InvoiceSmsWidgetComponent } from './widget/invoice-sms-widget/invoice-sms-widget.component';
import { InvoiceStateWidgetComponent } from './widget/invoice-state-widget/invoice-state-widget.component';

registerLocaleData(localeFi, 'fi', localeFiExtra);

@NgModule({
  declarations: [
    AppComponent,
    MainLayoutComponent,
    BreadcrumbComponent,
    ClientPageComponent,
    ClientRepresentativePageComponent,
    ClientRepresentativePageComponent,
    ClientsListPageComponent,
    ClientsPageComponent,
    EndCustomerDuplicatesPageComponent,
    EndCustomerMergePageComponent,
    LightEntrepreneurClientPageComponent,
    AutofocusDirective,
    FullScreenLoaderComponent,
    InvoicePageComponent,
    InvoiceCreatePageComponent,
    InvoicesListPageComponent,
    LeadSourcePageComponent,
    LeadSourcesListPageComponent,
    MultiSelectComponent,
    MultiSelectFilterComponent,
    NotFoundPageComponent,
    LabelPipe,
    PageComponent,
    PartnerPageComponent,
    PartnerSellerPageComponent,
    PartnersListPageComponent,
    PaymentHistoriesListPageComponent,
    PaymentHistoryImportPageComponent,
    OutgoingPaymentsListPageComponent,
    IncomingPaymentsListPageComponent,
    PcrTasksListPageComponent,
    OperationalTasksListPageComponent,
    InvoiceStateListFilterComponent,
    TextListFilterComponent,
    ResetListFilterComponent,
    SettingsPageComponent,
    TemplatesListPageComponent,
    EmailTemplatePageComponent,
    SmsTemplatePageComponent,
    ClearPageComponent,
    LoginComponent,
    MainBarComponent,
    MainNavComponent,
    PageBarComponent,
    LoaderComponent,
    TextComponent,
    ListFilterGroupComponent,
    ListLoaderComponent,
    DashboardPageComponent,
    InvoiceInfoWidgetComponent,
    InvoiceInfoV2WidgetComponent,
    CardTitleComponent,
    InvoiceStateWidgetComponent,
    InvoiceClientWidgetComponent,
    InvoiceLeClientWidgetComponent,
    ResourceAutoCompleteComponent,
    ClientAutoCompleteComponent,
    EndCustomerAutoCompleteComponent,
    LeadSourceAutoCompleteComponent,
    PartnerAutoCompleteComponent,
    InvoiceEndCustomerWidgetComponent,
    InvoiceAttachmentsWidgetComponent,
    InvoiceAttachmentsV2WidgetComponent,
    FileUploadDirective,
    InvoiceSmsWidgetComponent,
    InvoiceEmailWidgetComponent,
    InvoiceEmailV2WidgetComponent,
    InvoicePaymentHistoryWidgetComponent,
    InvoiceOutgoingPaymentWidgetComponent,
    InvoiceFinancingWidgetComponent,
    InvoiceFinancingV2WidgetComponent,
    InvoiceNotificationsWidgetComponent,
    InvoiceCommentsV2WidgetComponent,
    InvoicePresenceRosterWidgetComponent,
    InvoiceAiChatWidgetComponent,
    ClientExportsWidgetComponent,
    ClientExportsModalComponent,
    ConfirmationDialogComponent,
    UserInfoComponent,
    LeadInformationsListPageComponent,
    InvoiceClientInvoicesWidgetComponent,
    InvoiceLeClientInvoicesWidgetComponent,
    InvoiceEndCustomerInvoicesWidgetComponent,
    InvoiceEndCustomerCreditInsuranceWidgetComponent,
    InvoicePcrWidgetComponent,
    InvoiceConfigurationListFilterComponent,
    EmailStateLabelComponent,
    PaymentTransferStatusLabelComponent,
    OutgoingPaymentStatusLabelComponent,
    CardWidgetComponent,
    SignDocumentRequestDialogComponent,
    MoneyPipe,
    InvoiceNotificationsComponent,
    SourceNameComponent,
    ReturnUrlRedirectComponent,
    ClientHistoryComponent,
    CompanyFormFieldsComponent,
    ClientEditDialogComponent,
    LightEntrepreneurClientEditDialogComponent,
    AdvanceInvoiceNumberDialogComponent,
    InvoiceFinancingHoldsWidgetComponent,
    PlaceFinancingHoldDialogComponent,
    ReleaseFinancingHoldDialogComponent,
    ReleaseFinancingHoldsDialogComponent,
  ],
  imports: [
    AppRoutingModule,
    AvatarModule,
    BrowserModule,
    MarkdownModule.forRoot(),
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatBadgeModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    TextFieldModule,
    MatListModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatTableModule,
    MatSortModule,
    MatTabsModule,
    MatTableModule,
    MatToolbarModule,
    FlexLayoutModule,
    MatMomentDateModule,
    MaterialModule,
    CoreModule.forRoot(),
    SharedModule,
    ApiModule.forRoot({ rootUrl: environment.api.url }),
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'fi' },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true, strict: true } },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: LOCALE_ID, useValue: 'fi' },
    { provide: HTTP_INTERCEPTORS, useClass: JsonDateInterceptor, multi: true },
    AlertService,
    ClientRepresentativesResource,
    ClientsResource,
    CreditInsurancesResource,
    EmailLogsResource,
    EmailStatesResource,
    EmailStatesMapCache,
    EmailTemplatesResource,
    EndCustomersResource,
    EndCustomerContactsResource,
    FinancingMethodsMapCache,
    FinancingMethodsResource,
    InvoicesResource,
    InvoiceAttachmentsResource,
    InvoiceAttachmentTypesResource,
    InvoiceAttachmentTypesMapCache,
    InvoiceConfigurationsMapCache,
    InvoiceConfigurationsResource,
    InvoiceStatesMapCache,
    InvoiceStatesResource,
    InvoiceStateLogsResource,
    LeadSourcesResource,
    NotificationLogsResource,
    PartnersResource,
    PaymentHistoriesResource,
    PaymentHistoryImportsResource,
    PaymentTransferStatusesMapCache,
    PaymentTransferStatusesResource,
    PenaltyInterestMethodsMapCache,
    PenaltyInterestMethodsResource,
    InvoiceSubPageRollbackGuard,
    PermissionGuard,
    //    RouteGuard,
    StranglerGuard,
    SmsLogsResource,
    SmsTemplatesResource,
    UsersMapCache,
    UsersResource,
    ApplicationsResource,
    CountriesMapCache,
    CountriesResource,
    CompanyLookupResource,
    LeadInformationsResource,
    CurrencyPipe,
    MoneyPipe,
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() {
    // AI: All NgRedux logic has been removed and replaced with service-based state management.
  }
}
